import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }

    this.#maybeShowDialogOnUtmTrigger();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  #yLocation = 0;
  #distanceTop = 0;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    dialog.style.removeProperty('--current-translate-y');
    dialog.style.transform = '';
    dialog.classList.remove('dialog-modal--snapping-back', 'dialog-closing');

    this.#yLocation = 0;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    if (!this.#yLocation) {
      dialog.style.setProperty('--current-translate-y', '0px');
    }

    dialog.style.transform = '';

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    dialog.style.removeProperty('--current-translate-y');
    this.#yLocation = 0;

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Handles the start of the touch close gesture.
   *   */

  touchCloseStart() {
    const { dialog } = this.refs;

    document.documentElement.classList.add('dialog-moving');
    dialog.classList.add('dialog-modal--moving');
    this.#distanceTop = dialog.getBoundingClientRect().top;
  }

  /**
   * Handles the move of the touch close gesture.
   *
   * @param {TouchEvent} event - The touch event.
   */

  touchCloseMove(event) {
    const { dialog } = this.refs;

    this.#yLocation = (event.touches[0]?.clientY ?? 0) - this.#distanceTop;

    if (this.#yLocation > 0) {
      dialog.style.setProperty('--current-translate-y', `${this.#yLocation}px`);
    }
  }

  /**
   * Handles the end of the touch close gesture.
   */
  async touchCloseEnd() {
    const { dialog } = this.refs;

    const threshold = (window.innerHeight / 5) * 2;

    document.documentElement.classList.remove('dialog-moving');
    dialog.classList.remove('dialog-modal--moving');

    if (this.#yLocation > threshold) {
      dialog.style.transform = '';
      this.closeDialog();
    } else if (this.#yLocation > 0) {
      dialog.style.transform = '';
      dialog.classList.add('dialog-modal--snapping-back');

      await onAnimationEnd(dialog, undefined, { subtree: false });

      dialog.classList.remove('dialog-modal--snapping-back');
      dialog.style.removeProperty('--current-translate-y');
      this.#yLocation = 0;
    } else {
      dialog.style.removeProperty('--current-translate-y');
    }
  }

  #maybeShowDialogOnUtmTrigger() {
    const utmRules = this.dataset.utmRules ? JSON.parse(this.dataset.utmRules) : undefined;

    if (utmRules) {
      const { campaign, medium, source } = utmRules;

      const url = new URL(window.location.href);
      const params = new URLSearchParams(url.search);

      const containsUtmCampaign = campaign.find((/** @type {string} */ campaign) =>
        params.get('utm_campaign')?.includes(campaign)
      );

      if (
        containsUtmCampaign &&
        source?.includes(params.get('utm_source') ?? '') &&
        medium?.includes(params.get('utm_medium') ?? '')
      ) {
        this.showDialog();
      }
    }
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);

/**
 * A custom element that manages a global dialog wrapper that ensures dialogs can be
 * opened with a hash link.
 *
 * @extends Component
 */

class DialogGlobalWrapper extends Component {
  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      'click',
      (event) => {
        const link = /** @type {HTMLAnchorElement} */ (event.target).closest("a[href^='#']");
        if (!link) return;

        const id = link.getAttribute('href')?.slice(1);
        const dialog = /** @type {DialogComponent} */ (this.querySelector(`dialog-component#${CSS.escape(id ?? '')}`));

        if (!dialog) return;

        event.preventDefault();
        dialog.showDialog();
      },
      { capture: true }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }
}

if (!customElements.get('dialog-global-wrapper')) {
  customElements.define('dialog-global-wrapper', DialogGlobalWrapper);
}
